import React, { useCallback, useEffect, useRef, useState } from "react";
import { apiGet, apiPost } from "../../utils/apiClient";
import "./MinimapLabelTool.css";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ObjectType =
  | "turret"
  | "inhibitor"
  | "nexus"
  | "champion_blue"
  | "champion_red"
  | "ward_stealth_blue"
  | "ward_stealth_red"
  | "ward_control_blue"
  | "ward_control_red"
  | "jungle_camp";

type PlacedObject = {
  id: string;
  type: ObjectType;
  x: number; // normalised 0-1
  y: number;
  team?: "blue" | "red"; // inferred from position for turret/inhib/nexus
  plates?: number; // 0-5 for turrets
};

type LandmarkConfig = {
  objects: PlacedObject[];
  imageWidth?: number;
  imageHeight?: number;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const OBJECT_TYPES: { value: ObjectType; label: string; color: string }[] = [
  { value: "turret", label: "Turret", color: "#f59e0b" },
  { value: "inhibitor", label: "Inhibitor", color: "#8b5cf6" },
  { value: "nexus", label: "Nexus", color: "#ec4899" },
  { value: "champion_blue", label: "Champion (Blue)", color: "#3b82f6" },
  { value: "champion_red", label: "Champion (Red)", color: "#ef4444" },
  { value: "ward_stealth_blue", label: "Stealth Ward (Blue)", color: "#22d3ee" },
  { value: "ward_stealth_red", label: "Stealth Ward (Red)", color: "#f87171" },
  { value: "ward_control_blue", label: "Control Ward (Blue)", color: "#06b6d4" },
  { value: "ward_control_red", label: "Control Ward (Red)", color: "#dc2626" },
  { value: "jungle_camp", label: "Jungle Camp", color: "#a3e635" },
];

const OBJ_RADIUS: Record<ObjectType, number> = {
  turret: 8,
  inhibitor: 10,
  nexus: 12,
  champion_blue: 11,
  champion_red: 11,
  ward_stealth_blue: 6,
  ward_stealth_red: 6,
  ward_control_blue: 6,
  ward_control_red: 6,
  jungle_camp: 7,
};

function colorForObj(obj: PlacedObject): string {
  const entry = OBJECT_TYPES.find((o) => o.value === obj.type);
  if (!entry) return "#fff";
  if (obj.type === "turret" || obj.type === "inhibitor" || obj.type === "nexus") {
    // Team colour based on map diagonal: blue = bottom-left, red = top-right
    const team = obj.x + obj.y > 1.0 ? "blue" : "red";
    return team === "blue" ? "#3b82f6" : "#ef4444";
  }
  return entry.color;
}

let _idCounter = 0;
function nextId(): string {
  return `obj_${++_idCounter}_${Date.now()}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type Props = {
  clipId: string | null;
};

const MinimapLabelTool: React.FC<Props> = ({ clipId }) => {
  const [objects, setObjects] = useState<PlacedObject[]>([]);
  const [activeType, setActiveType] = useState<ObjectType>("turret");
  const [turretPlates, setTurretPlates] = useState(5);
  const [zoom, setZoom] = useState(3);
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [imgNatural, setImgNatural] = useState<{ w: number; h: number } | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Load a minimap crop to use as background
  useEffect(() => {
    if (!clipId) {
      setImgSrc(null);
      return;
    }
    // Use the first available debug minimap crop
    setImgSrc(`/api/macro/minimap-label-bg?clipId=${encodeURIComponent(clipId)}`);
  }, [clipId]);

  // Load saved landmark config
  useEffect(() => {
    if (!clipId) return;
    void (async () => {
      try {
        const data = await apiGet(`/api/macro/minimap-landmarks?clipId=${encodeURIComponent(clipId)}`);
        if (data.objects && Array.isArray(data.objects)) {
          setObjects(data.objects as PlacedObject[]);
        }
      } catch {
        // No saved config yet
      }
    })();
  }, [clipId]);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImgNatural({ w: img.naturalWidth, h: img.naturalHeight });
  }, []);

  // Redraw canvas
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imgNatural) return;
    canvas.width = imgNatural.w;
    canvas.height = imgNatural.h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (const obj of objects) {
      const px = obj.x * canvas.width;
      const py = obj.y * canvas.height;
      const r = OBJ_RADIUS[obj.type] || 8;
      const col = colorForObj(obj);
      const isSelected = obj.id === selectedId;

      ctx.beginPath();
      ctx.arc(px, py, r, 0, Math.PI * 2);
      ctx.fillStyle = col + "88";
      ctx.fill();
      ctx.strokeStyle = isSelected ? "#fff" : col;
      ctx.lineWidth = isSelected ? 2.5 : 1.5;
      ctx.stroke();

      // Label
      ctx.font = "bold 9px sans-serif";
      ctx.fillStyle = "#fff";
      ctx.textAlign = "center";
      let label = obj.type.charAt(0).toUpperCase();
      if (obj.type === "turret" && obj.plates != null) label = `T${obj.plates}`;
      else if (obj.type === "inhibitor") label = "I";
      else if (obj.type === "nexus") label = "N";
      else if (obj.type.startsWith("champion")) label = "C";
      else if (obj.type.startsWith("ward_stealth")) label = "ws";
      else if (obj.type.startsWith("ward_control")) label = "wc";
      else if (obj.type === "jungle_camp") label = "J";
      ctx.fillText(label, px, py + 3);
    }
  }, [objects, imgNatural, selectedId]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  // Click to place or select
  const onCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas || !imgNatural) return;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const cx = (e.clientX - rect.left) * scaleX;
      const cy = (e.clientY - rect.top) * scaleY;
      const nx = cx / canvas.width;
      const ny = cy / canvas.height;

      // Check if clicking on existing object
      for (const obj of objects) {
        const dx = obj.x - nx;
        const dy = obj.y - ny;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const hitRadius = (OBJ_RADIUS[obj.type] || 8) / canvas.width * 1.5;
        if (dist < hitRadius) {
          setSelectedId(obj.id);
          return;
        }
      }

      // Place new object
      const newObj: PlacedObject = {
        id: nextId(),
        type: activeType,
        x: Math.max(0, Math.min(1, nx)),
        y: Math.max(0, Math.min(1, ny)),
      };
      if (activeType === "turret") {
        newObj.plates = turretPlates;
      }
      setObjects((prev) => [...prev, newObj]);
      setSelectedId(newObj.id);
    },
    [objects, activeType, turretPlates, imgNatural],
  );

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    setObjects((prev) => prev.filter((o) => o.id !== selectedId));
    setSelectedId(null);
  }, [selectedId]);

  const save = useCallback(async () => {
    if (!clipId) return;
    try {
      await apiPost("/api/macro/minimap-landmarks", { clipId, objects });
      setStatus("Saved!");
      setTimeout(() => setStatus(null), 2000);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Save failed");
    }
  }, [clipId, objects]);

  const generate = useCallback(async () => {
    if (!clipId) return;
    setGenerating(true);
    setStatus("Generating synthetic data...");
    try {
      const res = await apiPost("/api/macro/minimap-generate", { clipId });
      setStatus(`Generated ${res.count ?? "?"} images`);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Generation failed");
    } finally {
      setGenerating(false);
    }
  }, [clipId]);

  if (!clipId) {
    return (
      <section className="minimap-label-tool">
        <h2>Minimap Label Tool</h2>
        <p className="macro-muted">Select a clip to start placing minimap objects.</p>
      </section>
    );
  }

  return (
    <section className="minimap-label-tool">
      <h2>Minimap Label Tool</h2>
      <p className="macro-muted">
        Click on the minimap to place objects. Turrets, inhibitors, and nexus positions are saved as
        fixed landmarks. Champions and wards are used for synthetic data generation.
      </p>

      {/* Toolbar */}
      <div className="mlt-toolbar">
        <div className="mlt-types">
          {OBJECT_TYPES.map((ot) => (
            <button
              key={ot.value}
              type="button"
              className={`mlt-type-btn ${activeType === ot.value ? "mlt-type-btn--active" : ""}`}
              style={{ borderColor: ot.color, color: activeType === ot.value ? "#fff" : ot.color, backgroundColor: activeType === ot.value ? ot.color : "transparent" }}
              onClick={() => setActiveType(ot.value)}
            >
              {ot.label}
            </button>
          ))}
        </div>
        {activeType === "turret" && (
          <div className="mlt-plates">
            <label>Plates:</label>
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                className={`mlt-plate-btn ${turretPlates === n ? "mlt-plate-btn--active" : ""}`}
                onClick={() => setTurretPlates(n)}
              >
                {n}
              </button>
            ))}
          </div>
        )}
        <div className="mlt-actions">
          <button type="button" onClick={deleteSelected} disabled={!selectedId}>
            Delete selected
          </button>
          <button type="button" onClick={() => setObjects([])}>
            Clear all
          </button>
          <button type="button" onClick={save}>
            Save landmarks
          </button>
          <button type="button" onClick={generate} disabled={generating}>
            {generating ? "Generating..." : "Generate training data"}
          </button>
        </div>
        <div className="mlt-zoom">
          <label>Zoom: {zoom.toFixed(1)}x</label>
          <input type="range" min={1} max={6} step={0.5} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
        </div>
        {status && <span className="mlt-status">{status}</span>}
      </div>

      {/* Summary */}
      <div className="mlt-summary">
        {OBJECT_TYPES.map((ot) => {
          const count = objects.filter((o) => o.type === ot.value).length;
          if (count === 0) return null;
          return (
            <span key={ot.value} className="mlt-count" style={{ color: ot.color }}>
              {ot.label}: {count}
            </span>
          );
        })}
        <span className="mlt-count">Total: {objects.length}</span>
      </div>

      {/* Canvas */}
      <div className="mlt-canvas-scroll">
        <div className="mlt-canvas-zoom" style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}>
          <div className="mlt-canvas-wrap" style={imgNatural ? { width: imgNatural.w, height: imgNatural.h } : undefined}>
            <img
              src={imgSrc || ""}
              alt="minimap"
              className="mlt-img"
              onLoad={onImageLoad}
              style={{ display: imgSrc ? "block" : "none" }}
            />
            <canvas ref={canvasRef} className="mlt-canvas" onClick={onCanvasClick} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default MinimapLabelTool;
